import React, { useState, useEffect } from 'react';
import { FileText, Save, ChevronDown } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardExpandable } from '@/components/ui/Card/Card';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { ResearchService } from '@/services/ResearchService';
import { useNotificationStore } from '@/stores/notificationStore';

interface RequirementsCardProps {
  eventId: string;
  initialNotes: string;
  onNotesUpdate: (notes: string) => void;
}

export function RequirementsCard({ eventId, initialNotes, onNotesUpdate }: RequirementsCardProps) {
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const researchService = new ResearchService();
  const notificationStore = useNotificationStore();

  // Track if there are unsaved changes
  const hasChanges = notes !== initialNotes;

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  async function handleSave() {
    if (!hasChanges) return;

    try {
      setSaving(true);
      await researchService.updateEventNotes(eventId, notes);
      onNotesUpdate(notes);
      notificationStore.success('Event notes saved successfully');
    } catch (error) {
      console.error('Failed to save notes:', error);
      notificationStore.error('Failed to save event notes. Please try again.', {
        title: 'Save Failed',
      });
    } finally {
      setSaving(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setNotes(e.target.value);
  }

  return (
    <Card>
      <CardHeader onClick={() => setIsExpanded(!isExpanded)} className="cursor-pointer hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2 flex-1">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <div className="flex-1">
            <CardTitle as="h3">Requirements & Notes</CardTitle>
          </div>
          {hasChanges && (
            <span className="text-xs text-warning-600 font-medium mr-2">
              Unsaved changes
            </span>
          )}
          <ChevronDown 
            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </CardHeader>
      <CardExpandable isExpanded={isExpanded}>
        <CardContent>
          <Textarea
            value={notes}
            onChange={handleChange}
            placeholder="Describe event requirements, preferences, and constraints...&#10;&#10;Example:&#10;- Need 80+ rooms&#10;- Large conference space for 200 people&#10;- Near airport (<30km)&#10;- Beach access preferred&#10;- 4-5 star hotels"
            rows={8}
            className="resize-none text-sm"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {hasChanges ? 'Click Save to update' : 'No changes'}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              isLoading={saving}
              leftIcon={<Save className="w-3 h-3" />}
            >
              Save
            </Button>
          </div>
        </CardContent>
      </CardExpandable>
    </Card>
  );
}

